// ============================================================================
// @torre/expenses — Análisis del flujo: ingresos (A), gastos (B) y pérdidas (C)
// ============================================================================
// La portada de «Flujo» junta las tres piezas y las explica:
//
//   A  Ingresos   lo que entró de clientes y terceros
//   B  Gastos     lo que la empresa pagó
//   C  Pérdidas   la parte de B que no dejó nada (nómina sin horas que la
//                 justifiquen + gastos marcados como pérdida)
//
//   Flujo neto = A − B.  C no se resta otra vez: ya está dentro de B; lo que
//   hace es partir el gasto en «productivo» y «perdido».
//
// Lógica pura: devuelve los indicadores y una lista de hallazgos con código y
// parámetros; el texto lo pone la interfaz en el idioma del usuario.
// ============================================================================

import { round2, type CashflowSummary, type ExpenseCategory } from "./index";
import type { LossSummary } from "./losses";

export type InsightTone = "good" | "warn" | "bad" | "info";

export interface CashflowInsight {
  code:
    | "surplus"
    | "deficit"
    | "noIncome"
    | "coverage"
    | "lossesHigh"
    | "lossesSome"
    | "lossesNone"
    | "lossesUnknown"
    | "payerConcentration"
    | "topCategory"
    | "incomeTrend"
    | "spendTrend"
    | "negativeMonths";
  tone: InsightTone;
  params: Record<string, string | number>;
}

export interface CashflowAnalysisMonth {
  period: string;
  income: number;
  /** Gasto de la empresa (B). */
  spend: number;
  /** Parte del gasto que fue pérdida (C), sin pasar del gasto del mes. */
  loss: number;
  /** B − C. */
  productive: number;
  net: number;
  /** Flujo neto acumulado desde el inicio del rango. */
  cumulative: number;
}

export interface CashflowAnalysis {
  income: number;
  spend: number;
  /** `null` = no se pudieron leer las pérdidas (no es lo mismo que cero). */
  losses: number | null;
  productiveSpend: number;
  net: number;
  /** Flujo neto ÷ ingresos. `null` sin ingresos. */
  margin: number | null;
  /** Ingresos ÷ gastos. `null` sin gastos. */
  coverage: number | null;
  /** Pérdidas ÷ gastos. `null` sin gastos o sin pérdidas leídas. */
  lossShare: number | null;
  /** Meses con algún movimiento. */
  activeMonths: number;
  positiveMonths: number;
  negativeMonths: number;
  avgIncome: number;
  avgSpend: number;
  best: { period: string; net: number } | null;
  worst: { period: string; net: number } | null;
  /** Cambio de los últimos `TREND_MONTHS` meses cerrados contra los anteriores. */
  incomeTrend: number | null;
  spendTrend: number | null;
  topPayer: { payer: string; share: number } | null;
  topCategory: { category: ExpenseCategory; share: number } | null;
  months: CashflowAnalysisMonth[];
  insights: CashflowInsight[];
}

/** Meses que se comparan contra los anteriores para la tendencia. */
export const TREND_MONTHS = 3;
/** Desde qué parte del gasto las pérdidas son un problema serio. */
export const LOSS_ALERT_SHARE = 0.1;
/** Desde qué parte de los ingresos un solo cliente es un riesgo. */
export const PAYER_ALERT_SHARE = 0.5;
/** Cambio mínimo para hablar de tendencia. */
const TREND_THRESHOLD = 0.1;

const ratio = (a: number, b: number): number | null => (b > 0 ? Math.round((a / b) * 1000) / 1000 : null);
const avg = (xs: number[]) => (xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : 0);

/** Cambio relativo del promedio de los últimos meses contra los anteriores. */
function trend(values: number[]): number | null {
  if (values.length < TREND_MONTHS * 2) return null;
  const recent = avg(values.slice(-TREND_MONTHS));
  const before = avg(values.slice(-TREND_MONTHS * 2, -TREND_MONTHS));
  return before > 0 ? Math.round(((recent - before) / before) * 1000) / 1000 : null;
}

export function cashflowAnalysis(input: {
  flow: Pick<CashflowSummary, "months" | "payers" | "categories">;
  /** `null` o ausente = no se pudieron leer. */
  losses?: Pick<LossSummary, "months"> | null;
  /** Mes en curso (`YYYY-MM`): va en los totales pero no en la tendencia. */
  currentPeriod?: string | null;
}): CashflowAnalysis {
  const lossByPeriod = new Map((input.losses?.months || []).map((m) => [m.period, m.total]));
  let cumulative = 0;
  const months = input.flow.months.map<CashflowAnalysisMonth>((m) => {
    const spend = m.companySpend;
    const loss = round2(Math.min(Math.max(lossByPeriod.get(m.period) || 0, 0), Math.max(spend, 0)));
    const net = round2(m.income - spend);
    cumulative = round2(cumulative + net);
    return { period: m.period, income: m.income, spend, loss, productive: round2(spend - loss), net, cumulative };
  });

  const income = round2(months.reduce((s, m) => s + m.income, 0));
  const spend = round2(months.reduce((s, m) => s + m.spend, 0));
  const losses = input.losses ? round2(months.reduce((s, m) => s + m.loss, 0)) : null;
  const net = round2(income - spend);
  const active = months.filter((m) => m.income !== 0 || m.spend !== 0);
  const positiveMonths = active.filter((m) => m.net > 0).length;
  const negativeMonths = active.filter((m) => m.net < 0).length;
  const sorted = [...active].sort((a, b) => b.net - a.net);
  const closed = months.filter((m) => !input.currentPeriod || m.period < input.currentPeriod);

  const payerTotal = input.flow.payers.reduce((s, p) => s + p.total, 0);
  const top = input.flow.payers[0];
  const topPayer = top && payerTotal > 0 ? { payer: top.payer, share: ratio(top.total, payerTotal) as number } : null;
  const cat = [...input.flow.categories].sort((a, b) => b.company - a.company)[0];
  const topCategory = cat && spend > 0 && cat.company > 0 ? { category: cat.category, share: ratio(cat.company, spend) as number } : null;

  const analysis: CashflowAnalysis = {
    income,
    spend,
    losses,
    productiveSpend: round2(spend - (losses || 0)),
    net,
    margin: income > 0 ? Math.round((net / income) * 1000) / 1000 : null,
    coverage: ratio(income, spend),
    lossShare: losses == null ? null : ratio(losses, spend),
    activeMonths: active.length,
    positiveMonths,
    negativeMonths,
    avgIncome: round2(avg(active.map((m) => m.income))),
    avgSpend: round2(avg(active.map((m) => m.spend))),
    best: sorted.length ? { period: sorted[0].period, net: sorted[0].net } : null,
    worst: sorted.length ? { period: sorted[sorted.length - 1].period, net: sorted[sorted.length - 1].net } : null,
    incomeTrend: trend(closed.map((m) => m.income)),
    spendTrend: trend(closed.map((m) => m.spend)),
    topPayer,
    topCategory,
    months,
    insights: [],
  };
  analysis.insights = insightsOf(analysis);
  return analysis;
}

function insightsOf(a: CashflowAnalysis): CashflowInsight[] {
  const out: CashflowInsight[] = [];
  if (a.income === 0 && a.spend === 0) return out;

  if (a.income === 0) out.push({ code: "noIncome", tone: "warn", params: { spend: a.spend } });
  else if (a.net >= 0) out.push({ code: "surplus", tone: "good", params: { net: a.net, margin: a.margin ?? 0 } });
  else out.push({ code: "deficit", tone: "bad", params: { net: Math.abs(a.net), margin: Math.abs(a.margin ?? 0) } });

  if (a.coverage != null && a.income > 0) out.push({ code: "coverage", tone: a.coverage >= 1 ? "good" : "bad", params: { coverage: a.coverage } });

  if (a.losses == null) out.push({ code: "lossesUnknown", tone: "info", params: {} });
  else if (a.losses === 0) out.push({ code: "lossesNone", tone: "good", params: {} });
  else {
    const share = a.lossShare ?? 0;
    out.push({ code: share >= LOSS_ALERT_SHARE ? "lossesHigh" : "lossesSome", tone: share >= LOSS_ALERT_SHARE ? "bad" : "warn", params: { losses: a.losses, share, productive: a.productiveSpend } });
  }

  if (a.negativeMonths > 0) out.push({ code: "negativeMonths", tone: a.negativeMonths * 2 > a.activeMonths ? "bad" : "warn", params: { count: a.negativeMonths, total: a.activeMonths } });

  if (a.incomeTrend != null && Math.abs(a.incomeTrend) >= TREND_THRESHOLD) {
    out.push({ code: "incomeTrend", tone: a.incomeTrend > 0 ? "good" : "bad", params: { change: a.incomeTrend, months: TREND_MONTHS } });
  }
  if (a.spendTrend != null && Math.abs(a.spendTrend) >= TREND_THRESHOLD) {
    out.push({ code: "spendTrend", tone: a.spendTrend > 0 ? "warn" : "good", params: { change: a.spendTrend, months: TREND_MONTHS } });
  }

  if (a.topPayer && a.topPayer.share >= PAYER_ALERT_SHARE) out.push({ code: "payerConcentration", tone: "warn", params: { payer: a.topPayer.payer, share: a.topPayer.share } });
  if (a.topCategory) out.push({ code: "topCategory", tone: "info", params: { category: a.topCategory.category, share: a.topCategory.share } });
  return out;
}
